/* ------------------------------------------------------------------ */
/* Buyer Page Master – validation rules & helpers                      */
/* Limits are sized to the live Buyer page layout (procman.ai/buyer).  */
/* Tweak any number below – nothing else needs to change.              */
/* ------------------------------------------------------------------ */

const URL_HELP = 'Internal path ("/create-rfq") or full link ("https://…").';

/* ---------- Scalar text / url fields: "group.key" ---------- */
export const TEXT_RULES = {
  // Hero
  "hero.badge": { label: "Badge", required: true, min: 3, max: 40, help: "Short pill above the heading." },
  "hero.heading": { label: "Heading", required: true, min: 10, max: 60 },
  "hero.description": { label: "Description", required: true, min: 20, max: 200 },
  "hero.primary_cta_label": { label: "Primary CTA Label", required: true, min: 2, max: 20 },
  "hero.primary_cta_url": { label: "Primary CTA URL", type: "url", required: true, max: 50, help: URL_HELP },
  "hero.secondary_cta_label": { label: "Secondary CTA Label", required: true, min: 2, max: 20 },
  "hero.secondary_cta_url": { label: "Secondary CTA URL", type: "url", required: true, max: 50, help: URL_HELP },

  // Industries heading
  "industries.heading": { label: "Section Heading", required: true, min: 5, max: 80 },

  // Procurement guide banner (all optional – falls back to defaults on the live page)
  "banner.heading_line1": { label: "Heading Line 1", max: 60 },
  "banner.heading_line2": { label: "Heading Line 2", max: 60 },
  "banner.description": { label: "Section Description", max: 300 },
  "banner.badge": { label: "Badge", max: 30 },
  "banner.card_title": { label: "Card Title", max: 80 },
  "banner.title": { label: "Read-More Modal Title", max: 100 },
  "banner.richTextContent": { label: "Card / Modal Content", max: 3000 },
  "banner.cta_label": { label: "CTA Label", max: 30 },

  // FAQ + CTA card
  "faq_cta.faq_title": { label: "FAQ Heading", required: true, min: 5, max: 60 },
  "faq_cta.heading": { label: "CTA Heading", required: true, min: 5, max: 40 },
  "faq_cta.description": { label: "CTA Description", required: true, min: 10, max: 150 },
  "faq_cta.primary_cta_label": { label: "Primary Button Label", required: true, min: 2, max: 25 },
  "faq_cta.primary_cta_url": { label: "Primary Button URL", type: "url", required: true, max: 200, help: URL_HELP },
  "faq_cta.secondary_cta_label": { label: "Secondary Button Label", required: true, min: 2, max: 25 },
  "faq_cta.secondary_cta_url": { label: "Secondary Button URL", type: "url", required: true, max: 200, help: URL_HELP },

  // SEO (canonical_url is read-only, so it has no rule)
//   "seo.title": { label: "Title", required: true, min: 10, max: 60, help: "Ideal: 50–60 characters." },
//   "seo.description": { label: "Description", required: true, min: 50, max: 160, help: "Ideal: 120–160 characters." },
//   "seo.keywords": { label: "Keywords", max: 255, help: "Comma separated." },
//   "seo.robots": {
//     label: "Robots",
//     required: true,
//     max: 40,
//     pattern: /^(index|noindex)\s*,\s*(follow|nofollow)$/i,
//     patternMessage: 'Robots must look like "index,follow" or "noindex,nofollow".',
//   },
//   "seo.og_title": { label: "OG Title", max: 60 },
//   "seo.og_description": { label: "OG Description", max: 160 },
//   "seo.twitter_title": { label: "Twitter Title", max: 60 },
//   "seo.twitter_description": { label: "Twitter Description", max: 160 },
//   "seo.twitter_card": {
//     label: "Twitter Card",
//     required: true,
//     max: 30,
//     pattern: /^(summary|summary_large_image)$/,
//     patternMessage: 'Twitter Card must be "summary" or "summary_large_image".',
//   },
//   "seo.seo_content": { label: "SEO Content", max: 10000 },

//   // Schema
//   "schema.custom_json": { label: "Custom JSON-LD", max: 10000 },
};

/* ---------- Card / repeater item fields ---------- */
export const ITEM_RULES = {
  metric: {
    value: { label: "Value", required: true, max: 10, help: 'e.g. "10,000+"' },
    label: { label: "Label", required: true, min: 3, max: 20 },
  },
  pain_point: {
    title: { label: "Title", required: true, min: 3, max: 25 },
    description: { label: "Description", required: true, min: 10, max: 70 },
  },
  feature: {
    title: { label: "Feature Title", required: true, min: 3, max: 30 },
    description: { label: "Feature Description", required: true, min: 10, max: 100 },
  },
  working: {
    label: { label: "Step Label", required: true, min: 3, max: 40 },
    desc: { label: "Step Description", required: true, min: 10, max: 120 },
  },
  industry: {
    title: { label: "Industry Name", required: true, min: 3, max: 30 },
    subtitle: { label: "Subtitle", required: true, min: 3, max: 45 },
  },
  faq: {
    question: { label: "Question", required: true, min: 10, max: 150 },
    answer: { label: "Answer", required: true, min: 10, max: 800 },
  },
  benefit: {
    text: { label: "Text", required: true, min: 3, max: 30 },
  },
};

/* ---------- How many cards each list may hold ---------- */
export const REPEATER_RULES = {
  "metrics.items": { min: 3, max: 6, noun: "metric" },
  "pain_points.items": { min: 3, max: 6, noun: "pain point" },
  "features.items": { min: 4, max: 12, noun: "feature" },
  "working.items": { min: 3, max: 9, noun: "step" },
  "industries.items": { min: 4, max: 8, noun: "industry" },
  faqs: { min: 1, max: 20, noun: "FAQ" },
  "faq_cta.benefits": { min: 1, max: 3, noun: "benefit" },
};

/* ---------- Additional schema generator (only validated when a type is chosen) ---------- */
export const ADDITIONAL_SCHEMA_RULES = {
  Organization: {
    name: { label: "Organization name", required: true, max: 100 },
    url: { label: "Organization URL", type: "url", max: 200 },
    description: { label: "Organization description", max: 300 },
    logo: { label: "Logo URL", type: "url", max: 300 },
  },
  Service: {
    name: { label: "Service name", required: true, max: 100 },
    description: { label: "Service description", max: 300 },
    url: { label: "Service URL", type: "url", max: 200 },
    provider_name: { label: "Provider name", max: 100 },
  },
  SoftwareApplication: {
    name: { label: "Application name", required: true, max: 100 },
    description: { label: "Application description", max: 300 },
    url: { label: "Application URL", type: "url", max: 200 },
    application_category: { label: "Application category", max: 60 },
    operating_system: { label: "Operating system", max: 40 },
  },
};

/* ---------- Image specs ---------- */
const FORMATS = ["image/png", "image/jpeg", "image/webp"];

export const IMAGE_SPECS = {
  hero_desktop: { label: "Desktop hero image", formats: FORMATS, maxSizeMB: 5, minWidth: 1200, minHeight: 526 },
  hero_mobile: { label: "Mobile hero image", formats: FORMATS, maxSizeMB: 5, minWidth: 600, minHeight: 800 },
  metric_icon: { label: "Metric icon", formats: FORMATS, maxSizeMB: 1, minWidth: 32, minHeight: 32 },
  pain_point_icon: { label: "Pain-point icon", formats: FORMATS, maxSizeMB: 1, minWidth: 32, minHeight: 32 },
  working_icon: { label: "Workflow icon", formats: FORMATS, maxSizeMB: 1, minWidth: 32, minHeight: 32 },
  cta_benefit_icon: { label: "CTA benefit icon", formats: FORMATS, maxSizeMB: 1, minWidth: 24, minHeight: 24 },
  banner_image: { label: "Banner image", formats: FORMATS, maxSizeMB: 3, minWidth: 800, minHeight: 400 },
  industry_image: { label: "Industry image", formats: FORMATS, maxSizeMB: 5, minWidth: 540, minHeight: 576 },
  og_image: { label: "OG image", formats: FORMATS, maxSizeMB: 5,
    //  minWidth: 1200,
   // minHeight: 630
     },
  twitter_image: { label: "Twitter image", formats: FORMATS, maxSizeMB: 5,
    //  minWidth: 1200, minHeight: 630 
    },
};

/* ------------------------------------------------------------------ */
/* Validators                                                          */
/* ------------------------------------------------------------------ */
export const validateText = (value, rules, label = "This field") => {
  if (!rules) return null;
  const name = rules.label || label;
  const text = (value ?? "").toString().trim();
  if (!text) return rules.required ? `${name} is required.` : null;
  if (rules.min && text.length < rules.min) return `${name} must be at least ${rules.min} characters.`;
  if (rules.max && text.length > rules.max) return `${name} must be ${rules.max} characters or fewer.`;
  if (rules.pattern && !rules.pattern.test(text)) return rules.patternMessage || `${name} is not valid.`;
  return null;
};

export const validateUrl = (value, rules = {}, label = "URL") => {
  const name = rules.label || label;
  const url = (value ?? "").toString().trim();
  if (!url) return rules.required ? `${name} is required.` : null;
  if (/\s/.test(url)) return `${name} must not contain spaces.`;
  if (rules.max && url.length > rules.max) return `${name} must be ${rules.max} characters or fewer.`;
  if (/^https?:\/\//i.test(url)) {
    try {
      new URL(url);
      return null;
    } catch {
      return `${name} is not a valid link.`;
    }
  }
  if (/^(mailto:|tel:)/i.test(url)) return null;
  if ((url.startsWith("/") && !url.startsWith("//")) || url.startsWith("#")) return null;
  return `${name} must start with "/" (internal) or "https://" (external).`;
};

export const validateByRules = (value, rules, label) =>
  rules?.type === "url" ? validateUrl(value, rules, label) : validateText(value, rules, label);

export const validateRepeaterCount = (items, rules) => {
  if (!rules) return null;
  const count = Array.isArray(items) ? items.length : 0;
  const noun = rules.noun || "item";
  if (rules.min && count < rules.min) return `Add at least ${rules.min} ${noun}${rules.min > 1 ? "s" : ""}.`;
  if (rules.max && count > rules.max) return `Maximum ${rules.max} ${noun}s allowed (currently ${count}).`;
  return null;
};

const readDimensions = (file) =>
  new Promise((resolve) => {
    const src = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(src);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(src);
      resolve(null);
    };
    img.src = src;
  });

/** Returns an array of problems (empty array = file is OK). */
export const validateImageBeforeUpload = async (file, specKey) => {
  const spec = IMAGE_SPECS[specKey];
  const problems = [];
  if (!file || !spec) return problems;

  if (!spec.formats.includes(file.type)) {
    problems.push(`${spec.label} must be a PNG, JPEG or WebP file.`);
    return problems;
  }
  if (file.size > spec.maxSizeMB * 1024 * 1024) {
    problems.push(
      `${spec.label} must be under ${spec.maxSizeMB} MB (selected file is ${(file.size / 1048576).toFixed(1)} MB).`,
    );
  }
  const dims = await readDimensions(file);
  if (!dims) {
    problems.push("Select a valid image file.");
  } else if (dims.width < spec.minWidth || dims.height < spec.minHeight) {
    problems.push(
      `${spec.label} is too small (${dims.width} × ${dims.height}px). Minimum is ${spec.minWidth} × ${spec.minHeight}px.`,
    );
  }
  return problems;
};

/* ------------------------------------------------------------------ */
/* Whole-page validation → flat { path: message } map                  */
/* Paths: "hero.heading", "metrics.items[2].value", "faqs[0].answer"   */
/* List-level (count) errors live at the list path itself.             */
/* ------------------------------------------------------------------ */
const LISTS = [
  { path: "metrics.items", get: (c) => c.metrics?.items, rules: ITEM_RULES.metric },
  { path: "pain_points.items", get: (c) => c.pain_points?.items, rules: ITEM_RULES.pain_point },
  { path: "features.items", get: (c) => c.features?.items, rules: ITEM_RULES.feature },
  { path: "working.items", get: (c) => c.working?.items, rules: ITEM_RULES.working },
  { path: "industries.items", get: (c) => c.industries?.items, rules: ITEM_RULES.industry },
  { path: "faqs", get: (c) => c.faqs, rules: ITEM_RULES.faq },
  { path: "faq_cta.benefits", get: (c) => c.faq_cta?.benefits, rules: ITEM_RULES.benefit },
];

// IndustriesEditor exists in BuyerPageMaster.jsx but is not rendered on the page today.
// Its rules are ready – remove "industries" from this list the day the editor is rendered,
// otherwise a hidden field could block Save with an error the user can't see.
const UNRENDERED_GROUPS = ["industries"];

export const validateBuyerContent = (content) => {
  const errors = {};
  const put = (path, message) => {
    if (message) errors[path] = message;
  };

  // scalar fields
  Object.entries(TEXT_RULES).forEach(([ruleKey, rules]) => {
    const [group, key] = ruleKey.split(".");
    if (UNRENDERED_GROUPS.includes(group)) return;
    put(ruleKey, validateByRules(content[group]?.[key], rules, rules.label));
  });

  // custom JSON-LD must be parseable
  const customJson = (content.schema?.custom_json || "").trim();
  if (customJson && !errors["schema.custom_json"]) {
    try {
      JSON.parse(customJson);
    } catch {
      errors["schema.custom_json"] = "Custom JSON-LD must be valid JSON.";
    }
  }

  // cards
  LISTS.forEach(({ path, get, rules }) => {
    if (UNRENDERED_GROUPS.includes(path.split(".")[0])) return;
    const items = Array.isArray(get(content)) ? get(content) : [];
    put(path, validateRepeaterCount(items, REPEATER_RULES[path]));
    items.forEach((raw, index) => {
      const item = typeof raw === "string" ? { text: raw } : raw || {};
      Object.entries(rules).forEach(([key, fieldRules]) => {
        put(`${path}[${index}].${key}`, validateByRules(item[key], fieldRules, fieldRules.label));
      });
    });
  });

  // additional schema generator
  const type = content.schema?.additional_schema_type;
  const extra = ADDITIONAL_SCHEMA_RULES[type];
  if (extra) {
    Object.entries(extra).forEach(([key, rules]) => {
      put(`schema.additional.${key}`, validateByRules(content.schema?.additional_schema_data?.[key], rules, rules.label));
    });
  }

  return errors;
};